<script setup lang="ts">
import SemboLogo from '@apps/main/new-desktop/core/components/SemboLogo.vue';
import {
  useBrowserStore,
  usePaxStore,
  useRouterStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { CoreEventSource } from '@apps/main/new-desktop/core/models';

import { AppBackButton } from '@libs/ui';

import FirstDayTripPriceRibbon from '../../../core/components/FirstDayTripPriceRibbon.vue';
import { PaxSelectorButtonWhite } from '../../../modules/paxSelector';
import { HotelFiltersButtons } from '../../../modules/hotelFilters';
import {
  useHotelSelectionModalsStore,
  useHotelSelectionStore,
} from '../stores';

import HotelSelectionDestination from './HotelSelectionDestination.vue';

const hotelSelectionModalsStore = useHotelSelectionModalsStore();
const paxStore = usePaxStore();
const routerStore = useRouterStore();
const statsigEventsStore = useStatsigEventsStore();
const browserStore = useBrowserStore();
const hotelSelectionStore = useHotelSelectionStore();

function onBackButtonClick(): void {
  routerStore.navigateToTripOverview();
  statsigEventsStore.logHotelSelectionBackButtonClicked();
}
</script>

<template>
  <div class="hotel-selection-header">
    <div class="upper-section">
      <div class="buttons-wrapper">
        <SemboLogo class="logo" color="color-sembo-purple" />
        <div class="divider" />
        <AppBackButton
          type="black"
          :text="$t('planner.back')"
          :link="routerStore.getTripOverviewUrl()"
          font="font-label-m-bold"
          @click="onBackButtonClick"
        />

        <div class="divider" />
        <PaxSelectorButtonWhite
          :button-text="paxStore.getPaxesButtonSimpleText"
          :children-ages="paxStore.paxesChildrenAgesText"
          @click="hotelSelectionModalsStore.openPaxSelectorModal()"
        />
      </div>
      <div class="calendar-wrapper">
        <FirstDayTripPriceRibbon
          :show-month-select-dropdown="false"
          :source="CoreEventSource.HotelSelection"
        />
      </div>
    </div>
    <div v-if="browserStore.isSmallerThan1536px" class="lower-section">
      <HotelSelectionDestination
        v-if="hotelSelectionStore.hotelSelectionHeaderData"
        class="destination"
        :data="hotelSelectionStore.hotelSelectionHeaderData"
        @change-number-of-nights="hotelSelectionStore.changeNumberOfNights"
        @change-destination="
          hotelSelectionModalsStore.openDestinationSelectorModal(
            $event,
            hotelSelectionStore.editingIngredientId
          )
        "
      />
      <HotelFiltersButtons
        class="filters"
        :suggestion-type="hotelSelectionStore.listSuggestionType"
        :board-basis-options="hotelSelectionStore.hotelMealFilterOptions"
        :amenities-options="hotelSelectionStore.hotelAmenitiesFilterOptions"
        :accommodation-type-options="
          hotelSelectionStore.accommodationTypeFilterOptions
        "
        :ingredient="hotelSelectionStore.editingStayIngredient"
        @open-all-filters="hotelSelectionStore.openFilters()"
        @open-why-we-suggest-modal="
          hotelSelectionModalsStore.openWhyWeSuggestModal
        "
        @suggestion-type-selected="hotelSelectionStore.setListSuggestionType"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.hotel-selection-header {
  background-color: var(--color-white);
  border-bottom: 1px solid var(--color-neutral-30);
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .upper-section {
    display: flex;

    .buttons-wrapper {
      display: flex;
      align-items: center;

      margin-right: 0;
      height: 96px;

      border-bottom: 1px solid var(--color-scrollbar);
      border-right: 1px solid var(--color-scrollbar);

      .logo {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 0 var(--spacing-32);
        height: 100%;
        transition: all var(--transition-short);

        @include on-hover {
          cursor: pointer;
          background-color: var(--color-neutral-30);
        }
      }

      .divider {
        width: 1px;
        height: 100%;
        background-color: var(--color-neutral-30);
      }
    }

    .calendar-wrapper {
      overflow: hidden;
      padding: 0;
    }
  }
  .lower-section {
    width: 100%;
    overflow: hidden;
    display: flex;

    align-items: center;
    gap: var(--spacing-8);
    padding: 0 0 var(--spacing-8) var(--spacing-12);
    .destination {
      width: 35%;
    }
    .filters {
      width: 65%;
    }
  }
}
</style>
